import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP_NUMBER = '5585999325303';

export default function ExchangeSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const els = sectionRef.current?.querySelectorAll('.exchange-reveal');
      if (els) {
        gsap.fromTo(
          els,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const benefits = [
    'Programas em mais de 15 países',
    'High School, Universidade e Pós-Graduação',
    'Cursos de idiomas com certificação internacional',
    'Work & Study e Au Pair',
    'Acomodação selecionada e verificada',
    'Orientação completa pré e pós-embarque',
  ];

  return (
    <section id="intercambio" ref={sectionRef} className="relative w-full py-24 md:py-32 px-6 md:px-10 lg:px-16 bg-background-50 overflow-hidden">
      {/* Background subtle map */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <img
          src="https://readdy.ai/api/search-image?query=Very%20subtle%20minimalist%20world%20map%20outline%20in%20light%20gray%20on%20white%20background%2C%20clean%20lines%2C%20no%20labels%2C%20elegant%2C%20travel%20concept&width=1440&height=900&seq=exchange-bg-map&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <div className="exchange-reveal relative">
            <div className="w-full aspect-[4/5] overflow-hidden rounded-lg">
              <img
                src="https://storage.readdy-site.link/project_files/f4c63f4d-f0a6-4026-b0ec-674f67e16aff/bdce0cb6-992b-4125-81c5-56f5fd66fa7c_compressed_ChatGPT-Image-28-de-jul.-de-2026-15_36_18.webp"
                alt="Intercâmbio Ao Redor do Mundo"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-28 h-28 bg-secondary-100/60 rounded-lg -z-10"></div>
          </div>

          {/* Text */}
          <div>
            <span className="exchange-reveal inline-block text-xs tracking-[0.2em] uppercase text-secondary-600 font-label mb-4">
              Intercâmbio
            </span>
            <h2 className="exchange-reveal font-heading text-4xl md:text-5xl lg:text-6xl font-light text-foreground-900 leading-tight mb-6">
              Estude e viva uma<br />
              <span className="italic">experiência transformadora</span>
            </h2>
            <p className="exchange-reveal text-foreground-600 leading-relaxed text-base md:text-lg mb-8">
              Mais do que aprender um novo idioma, um intercâmbio é uma imersão cultural completa. Cuidamos de cada etapa para que você aproveite ao máximo essa jornada de crescimento pessoal e profissional.
            </p>

            <ul className="exchange-reveal space-y-4 mb-10">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3 text-foreground-700">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-secondary-500 text-lg"></i>
                  </div>
                  <span className="text-sm md:text-base">{benefit}</span>
                </li>
              ))}
            </ul>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de conhecer os programas de intercâmbio da Ao Redor do Mundo.`}
              target="_blank"
              rel="noopener noreferrer"
              className="exchange-reveal group inline-flex items-center gap-3 px-8 py-4 bg-secondary-500 text-white rounded-full text-sm font-medium tracking-wide hover:bg-secondary-600 transition-all duration-500 whitespace-nowrap cursor-pointer"
            >
              Conhecer programas
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}